import urls from './urls';
import { TYPE_MESSAGE } from '../constants/notificationTypes';
import axios from 'axios';
import { TYPE_GROUP_CHAT } from '../constants/chatTypes';
import { messageTypes } from '../constants';
import { TYPE_TEXT } from '../constants/messageTypes';
import { MAXIMUM_TEXT_REPLY_LENGTH } from '../constants/constants';
import { IApiResponse } from '../interfaces/IApiResponse';
import { EditMessagePayload } from 'redhotpie-shared-lib/types/message';

interface ExtraParam {
  mediaId?: string;
  mediaType?: string;
  messageObject?: any;
}

interface MessageDetail {
  MsgType: string;
  Data: any;
  MsgId: string;
  Time: string;
  ChatType: string;
  ConvId: string;
  ToJid?: string;
}

class messageApi {
  static deleteMessage(mgsId: string, params: any): Promise<IApiResponse> {
    const url = urls.DELETE_MESSAGE.replace('{messageId}', encodeURIComponent(mgsId));
    return axios.delete(url, {
      baseURL: urls.BASE_URL,
      headers: { isAnonymous: false },
      data: params,
    });
  }

  static updateMessage(mgsId: string, status: string, ownerXmppId: string, peerXmppId: string): Promise<IApiResponse> {
    const url = urls.UPDATE_MESSAGE.replace('{messageId}', encodeURIComponent(mgsId));
    return axios.put(
      url,
      {
        status,
        ownerXmppId,
        peerXmppId,
      },
      { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }
    );
  }

  static updateMessageStatus(mgsId: string, status: string, peerUserXmppId?: string, isLast?: boolean, conversationId?: string): Promise<IApiResponse> {
    const url = urls.UPDATE_MESSAGE_STATUS.replace('{messageId}', encodeURIComponent(mgsId));
    return axios.put(
      url,
      {
        status,
        peerUserXmppId,
        isLast,
        conversationId,
      },
      { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }
    );
  }

  static updateMessageState(mgsId: string, state: string, peerUserXmppId: string, isLast: boolean, conversationId: string): Promise<IApiResponse> {
    const url = urls.UPDATE_MESSAGE_STATE.replace('{messageId}', encodeURIComponent(mgsId));
    console.log(url, {
      state,
      peerUserXmppId,
      isLast,
      conversationId,
    });
    return axios.put(
      url,
      {
        state,
        peerUserXmppId,
        isLast,
        conversationId,
      },
      { baseURL: urls.BASE_URL, headers: { isAnonymous: false } }
    );
  }

  static notifyMessageSentGroupChat(message: string, roomJid: string, extraParam?: ExtraParam): Promise<IApiResponse> {
    const params: any = {
      RoomJID: roomJid,
      Message: message,
    };
    if (extraParam?.mediaId) {
      params.MediaID = extraParam.mediaId;
      params.MediaType = extraParam.mediaType;
    }
    if (extraParam?.messageObject) {
      params.MessageDetail = getMessageDetail(extraParam.messageObject);
    }
    const url = urls.NOTIFICATION_GROUP_MESSAGE;
    return axios.post(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static notifyMessageSent(receiverId: string, message: string, notificationType: number = TYPE_MESSAGE, conversationKey: string, extraParam?: ExtraParam): Promise<IApiResponse> {
    const params: any = {
      Receiver: receiverId,
      Message: message,
      NotificationType: notificationType,
      ConversationKey: conversationKey,
    };
    if (extraParam?.mediaId) {
      params.MediaID = extraParam.mediaId;
      params.MediaType = extraParam.mediaType;
    }
    if (extraParam?.messageObject) {
      params.MessageDetail = getMessageDetail(extraParam.messageObject);
    }
    const url = `${urls.NOTIFY_MESSAGE_SENT}?time=${new Date().getTime()}`;
    return axios.post(url, params, { baseURL: urls.BASE_RHPURL, headers: { isAnonymous: false } });
  }

  static lookUpMessageByCondition({ receiverXmppId, msgType, msgMetaData }: { receiverXmppId: string; msgType: string; msgMetaData?: string }): Promise<IApiResponse> {
    let url = urls.LOOKUP_MESSAGE_BY_CONDITION + `/${encodeURIComponent(receiverXmppId)}?msgType=${msgType}`;
    if (msgMetaData) {
      url = url + `&msgMetaData=${encodeURIComponent(msgMetaData)}`;
    }
    return axios.get(url, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }

  static patchEditMessage(mgsId: string, message: EditMessagePayload): Promise<IApiResponse> {
    const url = urls.UPDATE_MESSAGE.replace('{messageId}', encodeURIComponent(mgsId));
    return axios.patch(url, message, { baseURL: urls.BASE_URL, headers: { isAnonymous: false } });
  }
}

function getMessageDetail(message: any): MessageDetail | undefined {
  if (!message?.body?.type) {
    return undefined;
  }
  const isGroup = message.type === TYPE_GROUP_CHAT;
  const result: MessageDetail = {
    MsgType: message.body.type,
    Data: {
      Expts: message.expts,
      IsRecvMatching: !!message.body.isRecvMatching,
      IsSenderMatching: !!message.body.isSenderMatching,
      RecvMemberLevel: message.body.recvMemberLevel,
      SenderMemberLevel: message.body.senderMemberLevel,
      ProfilePhotoComment: message.profilePhotoComment,
    },
    MsgId: message.id,
    Time: message.time,
    ChatType: message.type,
    ConvId: message.convId,
  };

  if (message.refMsgId) {
    result.Data.QuotedMessage = {
      RefMsgId: message.refMsgId,
      RefUserId: message.refUserId,
      RefMsgType: message.refMsgType,
      RefMsgShortDesc: message.refMsgType === TYPE_TEXT ? message.refMsgShortDesc.slice(0, MAXIMUM_TEXT_REPLY_LENGTH) : message.refMsgShortDesc,
      RefDisplayName: message.refDisplayName,
      RefMsgExpTs: message.refMsgExpTs,
      RefMsgTitle: message.refMsgTitle,
    };
  }

  try {
    switch (message.body.type) {
      case messageTypes.TYPE_TEXT:
        result.Data = {
          ...result.Data,
          Text: message.body.data,
        };
        break;
      case messageTypes.TYPE_IMAGE:
        result.Data = {
          ...result.Data,
          Image: {
            PhotoID: message?.photoData?.photoId || message.photoId,
            ThumbUrl: message?.photoData?.thumbUrl || message?.body?.data,
            FullUrl: message?.photoData?.fullUrl || message?.body?.data,
          },
        };
        break;
      case messageTypes.TYPE_VIDEO_FILE:
        result.Data = {
          ...result.Data,
          VideoFile: {
            VideoID: message.body.data.videoId,
            ThumbUrl: message.body.data.thumbUrl,
            Duration: message.body.data.duration,
            OwnerUserId: message.body.data.ownerUserId,
          },
        };
        break;

      case messageTypes.TYPE_AUDIO_FILE:
        result.Data = {
          ...result.Data,
          AudioFile: {
            FileName: message.body.data.fileName,
            Duration: message.body.data.duration,
            OwnerUserId: message.body.data.ownerUserId,
          },
        };
        break;
      case messageTypes.TYPE_GALLERY:
        result.Data = {
          ...result.Data,
          Gallery: {
            GalleryID: message.body.data.galleryId,
            ImageCount: message.body.data.imageCount,
            Name: message.body.data.name,
            FriendId: message.body.data.friendId,
          },
        };
        break;
      case messageTypes.TYPE_GIFT:
        result.Data = {
          ...result.Data,
          Gift: {
            Data: message.body.data.data,
            Description: message.body.data.description,
            Diamonds: message.body.data.diamonds,
            GiftId: message.body.data.giftId,
            ImageURL: message.body.data.imageURL,
            Level: message.body.data.level,
            Name: message.body.data.name,
            Price: message.body.data.price,
          },
        };
        break;
      case messageTypes.TYPE_GIF:
        result.Data = {
          ...result.Data,
          Gif: {
            fixedWidth: message.body.data.fixedWidth,
            original: message.body.data.original,
          },
        };
        break;
      case messageTypes.TYPE_VIDEO:
        result.Data = {
          ...result.Data,
          Video: {
            RequestType: 'end',
            Value: message.body.data,
          },
        };
        break;
      case messageTypes.TYPE_REACTIONS:
        result.Data = {
          ...result.Data,
          Reactions: {
            Unicode: message.body.data,
            Clear: !!message.isDeleteReaction,
            ToMsgId: message.reactOnMsgId,
          },
        };
        break;
      case messageTypes.TYPE_SHARE_LOCATION:
        result.Data = {
          ...result.Data,
          ShareLocation: { Latitude: message.body.data.latitude, Longitude: message.body.data.longitude },
        };
        break;
      case messageTypes.TYPE_SHARE_DATE:
        result.Data = {
          ...result.Data,
          ShareDate: { Id: message.body.data.id },
        };
        break;
      case messageTypes.TYPE_SHARE_EVENT:
        result.Data = {
          ...result.Data,
          ShareEvent: { Id: message.body.data.id },
        };
        break;
      case messageTypes.TYPE_SHARE_EVENT_TICKET:
        result.Data = {
          ...result.Data,
          ShareEventTicket: { Id: message.body.data.id },
        };
        break;
      case messageTypes.TYPE_TOOK_SCREEN_SHOT:
        result.Data = {
          ...result.Data,
        };
        break;
      case messageTypes.TYPE_INVITE_GROUP_CHAT:
        result.Data = {
          ...result.Data,
          InviteGroupChat: {
            RoomJid: message.body.data.roomJid,
            Value: message.body.data.value,
          },
        };
        break;
      case messageTypes.TYPE_INVITE_GROUP:
        result.Data = {
          ...result.Data,
          InviteGroup: {
            GroupId: message.body.data.groupId,
            Value: message.body.data.value,
          },
        };
        break;
      case messageTypes.TYPE_REMOVED_FROM_GROUP:
        result.Data = {
          ...result.Data,
          RemovedFromGroup: {
            UserId: message.body.data.userId,
            Value: message.body.data.value,
          },
        };
        break;
      case messageTypes.TYPE_LEAVE_GROUP_CHAT:
        result.Data = {
          ...result.Data,
          LeaveGroupChat: {
            UserId: message.body.data.userId,
            Value: message.body.data.value,
          },
        };
        break;
      case messageTypes.TYPE_JOINED_GROUP_CHAT:
        result.Data = {
          ...result.Data,
          JoinGroupChat: {
            UserId: message.body.data.userId,
            Value: message.body.data.value,
          },
        };
        break;
      case messageTypes.TYPE_INVITE_GROUP:
        result.Data = {
          ...result.Data,
          InviteGroup: {
            GroupId: message.body.data.groupId,
            Value: message.body.data.value,
          },
        };
        break;
      default:
        break;
    }

    if (isGroup) {
      result.ToJid = message.to;
    }
  } catch (e) {
    console.log(e);
  }
  return result;
}

export default messageApi;
